// Draws one Belmont's Curse area map as SVG: the game's map art, revealed chunk by chunk from
// the room grid in gamedata.json.
//
// Rooms sit on a grid of chunks. Each room has a position (its bottom-left chunk),
// a width and height in chunks, and a mask of which chunks exist. Chunk index 0 is
// the bottom-left chunk, counting left to right, then upwards.
import { esc } from "./game_tool/html"

const CELL = 18

// How much of the room art shows through per RoomChunkExplorationState
// (0 NoChunk, 1 Unexplored, 2 ExploredTemporary, 3 Validated). Rooms not visited: spoilers only.
const STATE_OPACITY = { 0: 0.3, 1: 0.55, 2: 1, 3: 1 }
const NOT_VISITED_OPACITY = 0.3
// World units each revealed chunk reaches past its edges: room outlines sit just outside the
// open space, so a room that fills its chunks has its outline in the neighbouring ones.
const REVEAL_PAD = 2
// Water, in world units: the game draws a wavy line about every 0.875 units of depth.
const WAVE_DEPTH = 0.875
const WAVE_LENGTH = 1.6
// Elevator chain link length, in SVG units.
const CHAIN_LINK = 0.8
// Door links that run along the same line get their own lanes, this far apart (SVG units),
// bending out over this distance from each end.
const LINK_LANE = 1.2
const LINK_BEND = 1.5

/**
 * Lateral offsets ([dx, dy] per segment, SVG units) that split up segments lying on the same line,
 * so overlapping door links read as separate lines.
 */
function linkLanes(segs) {
  const dirs = segs.map(({ ax, ay, bx, by }) => {
    const len = Math.hypot(bx - ax, by - ay) || 1
    return [(bx - ax) / len, (by - ay) / len]
  })
  const overlaps = (i, j) => {
    const [ux, uy] = dirs[i], s = segs[i], t = segs[j]
    if (Math.abs(ux * dirs[j][1] - uy * dirs[j][0]) > 0.01) return false // not parallel
    if (Math.abs(ux * (t.ay - s.ay) - uy * (t.ax - s.ax)) > 0.5) return false // not on one line
    const along = (x, y) => ux * (x - s.ax) + uy * (y - s.ay)
    const [t0, t1] = [along(t.ax, t.ay), along(t.bx, t.by)].sort((a, b) => a - b)
    return Math.min(t1, along(s.bx, s.by)) - Math.max(t0, 0) > 0.5
  }
  // Group segments that overlap, directly or through others.
  const group = segs.map((_, i) => i)
  const root = (i) => (group[i] === i ? i : (group[i] = root(group[i])))
  for (let i = 0; i < segs.length; i++) {
    for (let j = i + 1; j < segs.length; j++) if (overlaps(i, j)) group[root(j)] = root(i)
  }
  const members = {}
  segs.forEach((_, i) => (members[root(i)] ||= []).push(i))
  const offsets = segs.map(() => [0, 0])
  for (const [first, list] of Object.entries(members)) {
    const [ux, uy] = dirs[first]
    list.forEach((i, lane) => {
      const o = (lane - (list.length - 1) / 2) * LINK_LANE
      offsets[i] = [-uy * o, ux * o]
    })
  }
  return offsets
}

/** Path data for a door link: straight, or bending out to its lane and back in. */
function linkPath({ ax, ay, bx, by }, [ox, oy]) {
  if (!ox && !oy) return `M${ax} ${ay}L${bx} ${by}`
  const len = Math.hypot(bx - ax, by - ay) || 1
  const bend = Math.min(LINK_BEND, len / 3)
  const [ux, uy] = [((bx - ax) / len) * bend, ((by - ay) / len) * bend]
  return `M${ax} ${ay}L${ax + ux + ox} ${ay + uy + oy}L${bx - ux + ox} ${by - uy + oy}L${bx} ${by}`
}

/** Wavy lines filling a pool ({ x, y (top left), w, h } in world units), as SVG path data. */
function waves(pool, px, py) {
  const lines = Math.max(1, Math.round(pool.h / WAVE_DEPTH))
  const gap = pool.h / lines, amp = gap * 0.22
  const halves = Math.max(1, Math.round((2 * pool.w) / WAVE_LENGTH)), half = pool.w / halves
  const d = []
  for (let i = 0; i < lines; i++) {
    const y = pool.y - gap * (i + 0.5)
    d.push(`M${px(pool.x)} ${py(y)}`)
    for (let j = 0; j < halves; j++) {
      const x = pool.x + half * j
      d.push(`Q${px(x + half / 2)} ${py(y + (j % 2 ? -2 : 2) * amp)} ${px(x + half)} ${py(y)}`)
    }
  }
  return d.join("")
}

function cellsOf(room) {
  const cells = []
  for (let i = 0; i < room.chunks.length; i++) {
    if (room.chunks[i] !== "1") continue
    cells.push({ i, x: room.x + (i % room.w), y: room.y + Math.floor(i / room.w) })
  }
  return cells
}

/** Which icon a room gets on the map: "save", "ferryman", "boss" or null. */
export function roomIconKind(room) {
  if (/SaveRoom/.test(room.id)) return "save"
  if (/FastTravelRoom/.test(room.id)) return "ferryman"
  if (/Boss/.test(room.id)) return "boss"
  return null
}

/** An icon image centred on (x, y), `size` px square. */
function image(href, x, y, size) {
  return `<image href="${esc(href)}" x="${x - size / 2}" y="${y - size / 2}" width="${size}" height="${size}"/>`
}

/**
 * @param world     gamedata world: { chunkSize, rooms: [...],
 *                    map: { x, y, w, h, backdrop, rooms, roomsConcealed, water: [{ x, y, w, h }], waterColor,
 *                           patches: [{ src, x, y (top left), w, h }] room art missing from `rooms` } }
 * @param explored  roomId -> parsed exploration state from the save
 * @param options   { showAll, labelFor(room) -> string, markers: [{ x, y, kind, label }],
 *                    icons: { saveRoom, lastSaveRoom, ferryman, player, death } image URLs, lastSaveRoom: room ID,
 *                    objects: [{ x, y, label, done, icon | glyph + color }] in world units,
 *                    links: [{ a: { x, y }, b: { x, y } }] in world units,
 *                    chains: [{ x, top, bottom }] elevator shafts in world units,
 *                    exits: [{ x, y, dir: left|right|door, label }] in world units, zoom, ariaLabel,
 *                    secrets: draw secret passages open, landmarks: [{ src, x, y (top left), w, h }] in world units }
 */
export function renderAreaMap(world, explored, options) {
  const icons = options.icons || {}
  const zoom = options.zoom || 1
  const rooms = world.rooms.filter((r) => options.showAll || explored[r.id])
  if (!rooms.length) return ""
  const [cw, ch] = world.chunkSize || [24, 13]
  const art = world.map
  // Chunks keep their true 24x13 shape so the art lines up.
  const cellW = CELL, cellH = CELL * ch / cw
  // Icons match the game's: about 0.7 of a one-chunk corridor's height.
  const k = (0.7 * cellH) / CELL

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const r of rooms) {
    minX = Math.min(minX, r.x)
    minY = Math.min(minY, r.y)
    maxX = Math.max(maxX, r.x + r.w)
    maxY = Math.max(maxY, r.y + r.h)
  }
  const pad = 1
  minX -= pad; minY -= pad; maxX += pad; maxY += pad
  minX = Math.min(minX, art.x / cw)
  minY = Math.min(minY, art.y / ch)
  maxX = Math.max(maxX, (art.x + art.w) / cw)
  maxY = Math.max(maxY, (art.y + art.h) / ch)
  const px = (x) => (x - minX) * cellW
  const py = (y) => (maxY - y) * cellH // grid y points up, SVG y points down

  const parts = []
  const reveal = {} // opacity -> padded chunk rects of the room art to show
  const padX = (REVEAL_PAD / cw) * cellW, padY = (REVEAL_PAD / ch) * cellH
  for (const room of rooms) {
    const states = explored[room.id] ? explored[room.id].chunksExplorationState || [] : null
    const cells = cellsOf(room)
    // The art draws the room; its chunks are just hover targets.
    const body = []
    for (const c of cells) {
      const x = px(c.x), y = py(c.y + 1)
      body.push(`<rect class="game-tool__bc-chunk" data-cell="${c.x},${c.y}" x="${x}" y="${y}" width="${cellW}" height="${cellH}"/>`)
      const opacity = states ? STATE_OPACITY[states[c.i] || 0] : NOT_VISITED_OPACITY
      reveal[opacity] ||= []
      reveal[opacity].push(
        `<rect x="${x - padX}" y="${y - padY}" width="${cellW + 2 * padX}" height="${cellH + 2 * padY}"/>`
      )
    }
    let icon = ""
    // Icon rooms are full rectangles, so the middle of the room is inside it.
    const cx = px(room.x + room.w / 2), cy = py(room.y + room.h / 2)
    const kind = roomIconKind(room)
    const saveIcon = room.id === options.lastSaveRoom ? icons.lastSaveRoom || icons.saveRoom : icons.saveRoom
    if (kind === "save" && saveIcon) {
      icon = image(saveIcon, cx, cy, CELL * k)
    } else if (kind === "save") {
      icon = `<rect class="game-tool__bc-map-icon is-save" x="${cx - 4 * k}" y="${cy - 4 * k}" width="${8 * k}" height="${8 * k}" transform="rotate(45 ${cx} ${cy})"/>`
    } else if (kind === "ferryman" && icons.ferryman) {
      icon = image(icons.ferryman, cx, cy, CELL * k)
    } else if (kind === "boss") {
      icon = `<circle class="game-tool__bc-map-icon is-boss" cx="${cx}" cy="${cy}" r="${4.5 * k}"/>`
    }
    const label = esc(options.labelFor(room))
    parts.push(
      `<g class="game-tool__bc-room" data-map-tip-for="room" data-label="${label}">${body.join("")}${icon}</g>`
    )
  }

  // Door connections: a dashed line between the two ends, under the objects and markers.
  // A wide invisible stroke catches the hover.
  const segs = (options.links || []).map((l) => ({ ax: px(l.a.x / cw), ay: py(l.a.y / ch), bx: px(l.b.x / cw), by: py(l.b.y / ch) }))
  const lanes = linkLanes(segs)
  segs.forEach((seg, i) => {
    const d = linkPath(seg, lanes[i])
    const ends = `<circle cx="${seg.ax}" cy="${seg.ay}" r="0.5"/><circle cx="${seg.bx}" cy="${seg.by}" r="0.5"/>`
    parts.push(`<g class="game-tool__bc-link"><path class="is-hit" d="${d}"/><path class="is-line" d="${d}"/>${ends}</g>`)
  })
  // Elevator shafts: alternating face-on and edge-on links, overlapping so they read as interlocked.
  for (const c of options.chains || []) {
    const x = px(c.x / cw), top = py(c.top / ch), bottom = py(c.bottom / ch)
    const count = Math.max(1, Math.round((bottom - top) / CHAIN_LINK))
    const step = (bottom - top) / count
    const links = []
    for (let i = 0; i < count; i++) {
      const cy = top + step * (i + 0.5)
      links.push(i % 2
        ? `<path d="M${x} ${cy - step * 0.6}V${cy + step * 0.6}"/>`
        : `<ellipse cx="${x}" cy="${cy}" rx="${step * 0.35}" ry="${step * 0.6}"/>`)
    }
    parts.push(`<g class="game-tool__bc-chain">${links.join("")}</g>`)
  }
  // Advanced map objects, in world units like the markers.
  const size = CELL * 0.85 * k
  // The chunk a world position falls in, matching the chunks' data-cell, so the tooltip can list a cell's contents.
  const cellOf = (x, y) => `${Math.floor(x / cw)},${Math.floor(y / ch)}`
  for (const o of options.objects || []) {
    const ox = px(o.x / cw), oy = py(o.y / ch)
    const state = o.done === true ? "done" : o.done === false ? "todo" : "na"
    const body = o.icon
      ? image(o.icon, ox, oy, size)
      : `<circle cx="${ox}" cy="${oy}" r="${size / 2}" fill="${o.color || "#ccc"}"/>` +
        `<text x="${ox}" y="${oy}" text-anchor="middle" dominant-baseline="central">${esc(o.glyph || "")}</text>`
    const tick = o.done === true
      ? `<path class="game-tool__bc-tick" d="M${ox + 2 * k} ${oy + 4 * k}l${2.5 * k} ${2.5 * k}l${5 * k} ${-6 * k}"/>`
      : ""
    parts.push(
      `<g class="game-tool__bc-obj is-${state}" data-map-tip-for="object" data-cell="${cellOf(o.x, o.y)}" ` +
        `data-label="${esc(o.label)}"${o.done === true ? " data-done" : ""}>${body}${tick}</g>`
    )
  }
  // Exits to other areas: an arrow pointing out of the room (up for background doors). The
  // destination goes in the tooltip rather than on the map, where it would cover objects.
  for (const e of options.exits || []) {
    const ex = px(e.x / cw), ey = py(e.y / ch)
    const arrow = {
      right: `M${ex - 6} ${ey - 5}L${ex + 4} ${ey}L${ex - 6} ${ey + 5}Z`,
      left: `M${ex + 6} ${ey - 5}L${ex - 4} ${ey}L${ex + 6} ${ey + 5}Z`,
      door: `M${ex - 5} ${ey + 5}L${ex} ${ey - 5}L${ex + 5} ${ey + 5}Z`,
    }[e.dir] || ""
    parts.push(
      `<g class="game-tool__bc-exit" data-map-tip-for="exit" data-cell="${cellOf(e.x, e.y)}" data-label="${esc(e.label)}">` +
        `<path d="${arrow}"/></g>`
    )
  }
  for (const m of options.markers || []) {
    const mx = px(m.x / cw), my = py(m.y / ch)
    const attrs = `class="game-tool__bc-marker is-${m.kind}" data-map-tip-for="marker" data-cell="${cellOf(m.x, m.y)}" data-label="${esc(m.label)}"`
    if (icons[m.kind]) {
      parts.push(`<g ${attrs}>${image(icons[m.kind], mx, my, CELL * 1.3 * k)}</g>`)
    } else if (m.kind === "player") {
      parts.push(`<g ${attrs}><circle cx="${mx}" cy="${my}" r="${5 * k}"/></g>`)
    } else {
      const d = 4 * k
      parts.push(
        `<g ${attrs}>` +
          `<path d="M${mx - d} ${my - d}L${mx + d} ${my + d}M${mx + d} ${my - d}L${mx - d} ${my + d}"/></g>`
      )
    }
  }

  const w = (maxX - minX) * cellW, h = (maxY - minY) * cellH
  // Art is placed in world units; the room art only shows through explored chunks.
  const ax = px(art.x / cw), ay = py((art.y + art.h) / ch), aw = (art.w / cw) * cellW, ah = (art.h / ch) * cellH
  const place = `x="${ax}" y="${ay}" width="${aw}" height="${ah}" preserveAspectRatio="none"`
  // Padded rects overlap, so each opacity is applied to its group as a whole (overlaps don't
  // add up), most visible last so it wins where groups overlap.
  const groups = Object.keys(reveal).sort((a, b) => a - b)
    .map((o) => `<g opacity="${o}">${reveal[o].join("")}</g>`)
  const toPx = (x) => px(x / cw), toPy = (y) => py(y / ch)
  const placed = (l, attrs = "") =>
    `<image class="game-tool__bc-map-art" href="${esc(l.src)}" x="${toPx(l.x)}" y="${toPy(l.y)}" ` +
    `width="${(l.w / cw) * cellW}" height="${(l.h / ch) * cellH}"${attrs}/>`
  const artLayers =
    `<mask id="bc-map-reveal" maskUnits="userSpaceOnUse" x="0" y="0" width="${w}" height="${h}">` +
    `<g fill="#fff" shape-rendering="crispEdges">${groups.join("")}</g></mask>` +
    `<image class="game-tool__bc-map-art" href="${esc(art.backdrop)}" ${place}/>` +
    `<image class="game-tool__bc-map-art" href="${esc(options.secrets ? art.rooms : art.roomsConcealed)}" ${place} mask="url(#bc-map-reveal)"/>` +
    (art.patches || []).map((p) => placed(p, ' mask="url(#bc-map-reveal)"')).join("") +
    `<path class="game-tool__bc-map-art" d="${(art.water || []).map((p) => waves(p, toPx, toPy)).join("")}" ` +
    `fill="none" stroke="${esc(art.waterColor || "#d6c193")}" stroke-width="${(0.22 / cw) * cellW}" mask="url(#bc-map-reveal)"/>` +
    (options.landmarks || []).map((l) => placed(l)).join("")
  return (
    `<svg class="game-tool__bc-map-svg" viewBox="0 0 ${w} ${h}" style="${mapZoomStyle(w, h, zoom)};--bc-icon-scale:${k}" ` +
    `role="img" aria-label="${esc(options.ariaLabel || "Area map")}">` +
    artLayers +
    parts.join("") +
    `</svg>`
  )
}

/**
 * Inline width for a map SVG with a `width` × `height` viewBox. At 100% it fits the wrapper (a size
 * container) without blowing small maps up past 2.5x; zoom scales that fitted size, so every level
 * is continuous with the last. Past 100% the wrapper scrolls.
 */
function mapZoomStyle(width, height, zoom) {
  return `width:${mapZoomWidth(width, height, zoom)}`
}

export function mapZoomWidth(width, height, zoom) {
  return `calc(min(100cqw, ${100 * width / height}cqh, ${width * 2.5}px) * ${zoom})`
}

/** Chunk totals for an area: how many exist, and how many the save has explored. */
export function areaProgress(world, explored) {
  let total = 0, done = 0, roomsVisited = 0
  for (const room of world.rooms) {
    const states = explored[room.id] ? explored[room.id].chunksExplorationState || [] : null
    if (states) roomsVisited++
    for (let i = 0; i < room.chunks.length; i++) {
      if (room.chunks[i] !== "1") continue
      total++
      if (states && states[i] >= 2) done++
    }
  }
  return { total, done, roomsVisited, roomsTotal: world.rooms.length }
}
